import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AGENCY_NAME, APP_TITLE } from "@/lib/brand";

/**
 * Browser tab titles, in one place.
 *
 * House style: parts are separated by " - " and never by a bullet or middot, and a title is
 * never more than three parts long so it stays readable in a narrow tab.
 *
 *   /                     Clients - Website Editor - Review Presence
 *   /login                Sign in - Website Editor - Review Presence
 *   /t/dewalt             DeWalt Plumbing Services - Website Editor - Review Presence
 *   /t/dewalt/review      Review and publish - DeWalt Plumbing Services - Review Presence
 */
const SECTION_LABELS = {
  edit: "Editor",
  review: "Review and publish",
  preview: "Preview",
  history: "History",
  activity: "Activity",
  sync: "Sync from site",
  handover: "Handover",
  settings: "Settings",
};

const TOP_LEVEL_LABELS = {
  login: "Sign in",
  "not-invited": "No access",
  admin: "Who has access",
};

/** Belt and braces: whatever the source, a title never carries a bullet. Hyphens inside a
 *  client's own name (Smith-Jones Plumbing) are left exactly as they are. */
const dashify = (value) =>
  String(value)
    .replace(/\s*[\u00b7\u2022\u2219\u30fb]\s*/g, " - ")
    .replace(/\s{2,}/g, " ")
    .trim();

export const PageTitle = () => {
  const { pathname } = useLocation();
  const { tenants } = useAuth();

  useEffect(() => {
    const parts = pathname.split("/").filter(Boolean);
    let segments;

    if (parts[0] === "t" && parts[1]) {
      const tenant = tenants.find((t) => t.slug === parts[1]);
      const name = tenant?.name || parts[1];
      const section = SECTION_LABELS[parts[2]];
      segments = section ? [section, name, AGENCY_NAME] : [name, APP_TITLE, AGENCY_NAME];
    } else if (parts.length === 0) {
      segments = ["Clients", APP_TITLE, AGENCY_NAME];
    } else {
      segments = [TOP_LEVEL_LABELS[parts[0]], APP_TITLE, AGENCY_NAME];
    }

    document.title = segments.filter(Boolean).map(dashify).join(" - ");
  }, [pathname, tenants]);

  return null;
};
